import { cn } from "@/lib/utils";
import type { ComponentProps } from "react";
import { tv, type VariantProps } from "tailwind-variants";

function FieldSet({ className, ...props }: ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = "legend",
  ...props
}: ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn("py-2 max-lg:text-sm", className)}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        "group/field-group @container/field-group flex w-full flex-col gap-2",
        className,
      )}
      {...props}
    />
  );
}

const fieldVariants = tv({
  base: "relative gap-2 group/field flex has-[>[data-placeholder=true]]:[&>[data-slot=input],&>[data-slot=textarea],&>[role=combobox]]:placeholder:opacity-0 has-[>[data-placeholder=true]]:*:data-[slot=textarea]:pt-5 has-[>[data-placeholder=true]]:[&>[data-slot=input],&>[role=combobox]]:pt-4 has-[>[data-placeholder=true]]:[&>[data-slot=input],&>[data-slot=textarea],&>[role=combobox]]:h-12 has-[>[data-placeholder=true]]:[&>[data-slot=input],&>[data-slot=textarea],&>[role=combobox]]:pb-0!",
  variants: {
    orientation: {
      vertical: "flex-col *:w-full [&>.sr-only]:w-auto w-full",
      horizontal:
        "flex-row items-center *:data-[slot=field-label]:flex-auto has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
      responsive:
        "flex-col *:w-full [&>.sr-only]:w-auto @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto @md/field-group:*:data-[slot=field-label]:flex-auto @md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
    },
  },
  defaultVariants: {
    orientation: "vertical",
  },
});

function Field({
  className,
  orientation = "vertical",
  invalid = false,
  dirty = false,
  touched = false,
  filled = false,
  ...props
}: ComponentProps<"div"> &
  VariantProps<typeof fieldVariants> & {
    invalid?: boolean;
    dirty?: boolean;
    touched?: boolean;
    filled?: boolean;
  }) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      data-invalid={invalid}
      data-dirty={dirty}
      data-touched={touched}
      data-filled={filled}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  );
}

function FieldContent({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn(
        "group/field-content flex flex-1 flex-col gap-0.5 leading-snug",
        className,
      )}
      {...props}
    />
  );
}

const fieldLabelVariants = tv({
  base: "transition-all duration-150 has-data-checked:bg-primary/5 has-data-checked:border-primary/30 dark:has-data-checked:border-primary/20 dark:has-data-checked:bg-primary/10 group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50 has-[>[data-slot=field]]:border *:data-[slot=field]:p-2.5",
  variants: {
    placeholder: {
      true: "absolute top-3.5 left-3 text-muted-foreground pointer-events-none group-focus-within/field:top-1.5 group-focus-within/field:text-xs group-data-[filled=true]/field:top-1.5 group-data-[filled=true]/field:text-xs peer-is([type=file])]/field:top-1.5 peer-is([type=file])]/field:text-xs group-has-[input:not(:placeholder-shown)]/field:top-1.5 group-has-[input:not(:placeholder-shown)]/field:text-xs group-has-[button:is([role=combobox]):not([data-placeholder])]/field:top-1.5 group-has-[button:is([role=combobox]):not([data-placeholder])]/field:text-xs",
      false: "text-foreground leading-normal",
    },
    isInteractive: {
      true: "cursor-pointer hover:border-black",
    },
  },
  defaultVariants: {
    placeholder: false,
  },
});

function FieldLabel({
  className,
  placeholder = false,
  isInteractive = false,
  ...props
}: ComponentProps<"label"> & {
  placeholder?: boolean;
  isInteractive?: boolean;
}) {
  return (
    <label
      data-slot="field-label"
      data-placeholder={placeholder}
      className={cn(
        fieldLabelVariants({ placeholder, isInteractive }),
        className,
      )}
      {...props}
    />
  );
}

function FieldTitle({ className, ...props }: ComponentProps<"label">) {
  return (
    <label
      data-slot="field-label"
      className={cn(
        "flex w-fit items-center gap-2 text-sm leading-snug font-medium group-data-[disabled=true]/field:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

function FieldDescription({ className, ...props }: ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        "text-muted-foreground text-left text-sm leading-normal font-normal group-has-data-horizontal/field:text-balance [[data-variant=legend]+&]:-mt-1.5",
        "last:mt-0 nth-last-2:-mt-1",
        "[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4",
        className,
      )}
      {...props}
    />
  );
}

function FieldError({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn("text-destructive text-sm font-normal", className)}
      {...props}
    />
  );
}

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
};
